import { api, AxiosResponse, chatApi } from 'api/axios';
import {
  ChatChannel,
  ChatChannelCreationType,
  ChatMessage,
  ChatUser,
  UnreadCountApiRes,
} from 'model/chat';
import { useQuery } from 'react-query';
import { useAuth } from 'utils/AuthProvider';
import qs from 'qs';
import { request } from 'http';

export async function fetchChatToken(): Promise<AxiosResponse<ChatUser>> {
  const user = await api.get('/chat/fetch-token/');
  return user.data;
}

export const useChatToken = () => {
  const { token } = useAuth();
  const { data, ...rest } = useQuery<unknown, unknown, ChatUser>(
    ['chat-token'],
    () => fetchChatToken(),
    {
      enabled: !!token,
    },
  );
  return {
    chatUser: data,
    ...rest,
  };
};

export async function listChannel(
  token?: string,
  query?: {
    page?: number;
    pageSize?: number;
  },
): Promise<
  AxiosResponse<{
    total: number;
    page: number;
    pageSize: number;
    items: ChatChannel[];
  }>
> {
  let url = `api/channel`;
  const queryString = qs.stringify(query);
  if (queryString) {
    url += `/?${queryString}`;
  }
  const res = await chatApi.get(url, {
    params: {
      chatToken: token,
    },
  });
  return res.data;
}

export async function generateUploadUrlForMessage(
  data: { type: string },
  token: string,
) {
  return await chatApi.post('/api/fileupload/generate-upload-url', data, {
    params: {
      chatToken: token,
    },
  });
}

export const useChatChannel = (token?: string) => {
  const { data, ...rest } = useQuery<
    unknown,
    unknown,
    {
      total: number;
      page: number;
      pageSize: number;
      items: ChatChannel[];
    }
  >(['chat-channels'], () => listChannel(token, { page: 0, pageSize: 9999 }), {
    enabled: !!token,
  });

  return {
    chatChannels: data?.items ?? [],
    ...rest,
  };
};

export const listUnreadChannel = async (token?: string) => {
  const res = await chatApi.get('/api/channel/unread-channels', {
    params: {
      chatToken: token,
    },
  });
  return res.data;
};

export const useUnreadChannel = (token?: string) => {
  const { data, ...rest } = useQuery<unknown, unknown, UnreadCountApiRes>(
    ['unread-channels'],
    () => listUnreadChannel(token),
    {
      enabled: !!token,
    },
  );
  return {
    totalUnreadCount: data?.totalUnreadCount ?? 0,
    unreadChannels: data?.unreadChannels ?? [],
    ...rest,
  };
};

export async function listChatMessage(
  data: {
    cid: string;
    page: number;
    pageSize: number;
  },
  token: string | undefined,
) {
  const url = `/api/message?cid=${data.cid}&page=${
    data.page || 0
  }&pageSize=${data.pageSize || 100}`;
  const res = await chatApi.get(url, {
    params: {
      chatToken: token,
    },
  });
  return res.data;
}

export async function createChatMessage(
  data: Partial<ChatMessage>,
  token: string | undefined,
) {
  const url = `api/message`;
  const res = await chatApi.post(url, data, {
    params: {
      chatToken: token,
    },
  });
  return res.data;
}
export async function createChatChannel(
  data: Partial<ChatChannelCreationType>,
  token: string | undefined,
) {
  const url = `api/channel`;
  const res = await chatApi.post(url, data, {
    params: {
      chatToken: token,
    },
  });
  return res.data;
}

export async function markRead(cid: string, token: string | undefined) {
  const url = `api/channel/${cid}/mark-read`;
  const res = await chatApi.put(
    url,
    {},
    {
      params: {
        chatToken: token,
      },
    },
  );
  return res.data;
}
